import { type ReactNode, useState, useCallback, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { useQuery } from "@tanstack/react-query";
import { fetchQueueStatus, QueueStatus } from "../api";
import { useAuth } from "../contexts/AuthContext";
import UploadDialog from "./UploadDialog";
import { ErrorBoundary } from "./ErrorBoundary";

const NAV_ITEMS = [
  { path: "/", label: "总览", desc: "水温研判" },
  { path: "/news", label: "资讯", desc: "行业动态" },
  { path: "/sentiment", label: "舆情", desc: "产品舆情" },
  { path: "/policy", label: "政策", desc: "监管追踪" },
  { path: "/stats", label: "数据", desc: "宏观指标" },
  { path: "/industry", label: "行业", desc: "景气监测" },
];

// ─── Dark mode toggle ──────────────────────────────────────────────────────

function DarkModeToggle() {
  const [dark, setDark] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark")
  );

  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  useEffect(() => {
    const stored = localStorage.getItem("theme");
    if (stored === "dark" || (!stored && window.matchMedia("(prefers-color-scheme: dark)").matches)) {
      document.documentElement.classList.add("dark");
      setDark(true);
    }
  }, []);

  return (
    <button
      onClick={toggle}
      className="w-7 h-7 flex items-center justify-center rounded text-primary-50/60 hover:text-white hover:bg-white/[0.08] transition-colors"
      aria-label={dark ? "切换到浅色模式" : "切换到深色模式"}
    >
      {dark ? (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <circle cx="8" cy="8" r="3.5" />
          <path d="M8 1.5v1M8 13.5v1M1.5 8h1M13.5 8h1M3.4 3.4l.7.7M11.9 11.9l.7.7M3.4 12.6l.7-.7M11.9 4.1l.7-.7" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M13.5 9.5a5.5 5.5 0 01-7-7A5.5 5.5 0 1013.5 9.5z" />
        </svg>
      )}
    </button>
  );
}

// ─── Queue Status Indicator ────────────────────────────────────────────────

function QueueIndicator() {
  const [open, setOpen] = useState(false);

  const { data } = useQuery<QueueStatus>({
    queryKey: ["queueStatus"],
    queryFn: fetchQueueStatus,
    refetchInterval: (query) => {
      const d = query.state.data as QueueStatus | undefined;
      if (!d) return 15_000;
      return (d.queue_total > 0 || d.processing > 0) ? 8_000 : 60_000;
    },
    staleTime: 0,
  });

  if (!data) return null;

  const total = data.queue_total;
  const processing = data.processing;
  const busy = total > 0 || processing > 0;
  const hasFailed = data.failed > 0;

  const dotCls = busy
    ? "bg-blue-400 animate-pulse"
    : hasFailed
    ? "bg-orange-400"
    : "bg-emerald-400";

  let label = "";
  if (processing > 0 && total > 0) label = `${processing}/${total}`;
  else if (processing > 0) label = `分析中 ${processing}`;
  else if (total > 0) label = `队列 ${total}`;
  else label = `${data.done_24h} 完成`;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2 py-1 rounded text-2xs text-primary-50/50 hover:text-primary-50/80 transition-colors"
      >
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotCls}`} />
        <span className="hidden sm:inline font-mono tabular-nums">{label}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 w-56 bg-surface-raised rounded-lg shadow-float p-3.5 text-xs border-subtle animate-fade-in">
            <p className="font-semibold text-ink mb-3">分析队列</p>
            <div className="space-y-2">
              <Row label="优先队列" value={data.queue_priority}
                cls={data.queue_priority > 0 ? "text-primary-500 font-medium" : "text-ink-tertiary"} />
              <Row label="普通队列" value={data.queue_normal}
                cls={data.queue_normal > 0 ? "text-primary-500 font-medium" : "text-ink-tertiary"} />
              <Row label="正在处理" value={data.processing}
                cls={data.processing > 0 ? "text-primary-500 font-medium" : "text-ink-tertiary"} />
              <div className="h-px bg-border my-1" />
              <Row label="失败（待重试）" value={data.failed}
                cls={data.failed > 0 ? "text-warning font-medium" : "text-ink-tertiary"} />
              <div className="h-px bg-border my-1" />
              <Row label="过去1小时完成" value={data.done_1h} cls="text-positive" />
              <Row label="过去24小时完成" value={data.done_24h} cls="text-positive" />
            </div>
            {busy && (
              <p className="mt-3 text-2xs text-ink-tertiary">每 8 秒自动刷新</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value, cls }: { label: string; value: number; cls: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ink-tertiary">{label}</span>
      <span className={`font-mono tabular-nums ${cls}`}>{value}</span>
    </div>
  );
}

// ─── Layout ────────────────────────────────────────────────────────────────

export function Layout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const { user, isLoggedIn, canUpload, login, devLogin, logout } = useAuth();

  // Embed mode: ?embed=1 hides header for iframe usage
  const isEmbed = new URLSearchParams(location.search).get("embed") === "1";

  const handleSearch = useCallback(() => {
    const q = query.trim();
    if (q) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
      setQuery("");
      setMobileMenuOpen(false);
    }
  }, [query, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  if (isEmbed) {
    return (
      <div className="min-h-screen bg-surface">
        <main className="max-w-[1440px] mx-auto w-full px-4 sm:px-6 py-4 sm:py-5 overflow-x-hidden">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      {/* ── Header ── */}
      <header className="bg-primary-700 sticky top-0 z-50">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 h-11 flex items-center gap-3 sm:gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap shrink-0 group">
            <span className="flex items-center gap-1.5">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect width="16" height="16" rx="3" fill="#C9A96E" fillOpacity="0.9" />
                <path d="M4.5 5h7M4.5 8h5M4.5 11h6" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span className="text-sm font-semibold text-white tracking-tight">
                信贷情报
              </span>
            </span>
          </Link>

          <div className="w-px h-3.5 bg-white/[0.12] shrink-0 hidden sm:block" />

          {/* Desktop Nav */}
          <nav className="hidden sm:flex items-center gap-0.5">
            {NAV_ITEMS.map((item) => {
              const isActive = item.path === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={clsx(
                    "relative px-3 py-2 text-xs font-medium transition-colors duration-150",
                    isActive
                      ? "text-white"
                      : "text-primary-50/50 hover:text-primary-50/80"
                  )}
                >
                  {item.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-3 right-3 h-[2px] bg-accent rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="flex-1" />

          {/* Queue status */}
          <div className="hidden sm:block">
            <QueueIndicator />
          </div>

          <div className="w-px h-3.5 bg-white/[0.12] shrink-0 hidden md:block" />

          {/* Desktop Search */}
          <div className="relative flex items-center hidden md:block">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder="搜索..."
              className={clsx(
                "h-7 pl-3 pr-8 text-xs rounded bg-white/[0.08] text-primary-50 placeholder-primary-50/30",
                "border border-white/[0.06] transition-all duration-200 ease-out outline-none",
                focused
                  ? "border-white/20 bg-white/[0.12] w-52"
                  : "w-36 hover:bg-white/[0.1] hover:border-white/10"
              )}
            />
            <button
              onClick={handleSearch}
              className="absolute right-2 text-primary-50/40 hover:text-primary-50/70 transition-colors"
              aria-label="搜索"
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <circle cx="6.5" cy="6.5" r="4.5" />
                <line x1="10.5" y1="10.5" x2="14" y2="14" />
              </svg>
            </button>
          </div>

          {/* Dark mode toggle */}
          <DarkModeToggle />

          {/* Upload button (contributor+ only) */}
          {canUpload && (
            <button
              onClick={() => setUploadOpen(true)}
              className="hidden sm:flex items-center gap-1 h-7 px-2.5 rounded bg-white/[0.08] border border-white/[0.06] text-xs font-medium text-primary-50/70 hover:text-white hover:bg-white/[0.12] transition-colors"
            >
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <line x1="8" y1="3" x2="8" y2="13" />
                <line x1="3" y1="8" x2="13" y2="8" />
              </svg>
              上传
            </button>
          )}

          {/* Auth: login / user menu */}
          {isLoggedIn ? (
            <div className="hidden sm:flex items-center gap-1.5">
              <span className="text-2xs text-primary-50/50 truncate max-w-[80px]">{user?.name}</span>
              <button
                onClick={logout}
                className="text-2xs text-primary-50/40 hover:text-primary-50/70 transition-colors"
              >
                退出
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                // Try SSO first, fall back to dev login
                login();
              }}
              onContextMenu={(e) => {
                // Security: dev login via right-click only works in development
                if (import.meta.env.DEV) {
                  e.preventDefault();
                  devLogin();
                }
              }}
              className="hidden sm:flex items-center h-7 px-2.5 rounded bg-white/[0.08] border border-white/[0.06] text-xs text-primary-50/60 hover:text-white hover:bg-white/[0.12] transition-colors"
              title={import.meta.env.DEV ? "左键：企业微信登录 | 右键：开发模式登录" : "企业微信登录"}
            >
              登录
            </button>
          )}

          {/* Mobile: hamburger */}
          <div className="flex items-center gap-1.5 sm:hidden">
            <QueueIndicator />
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="w-8 h-8 flex items-center justify-center rounded text-primary-50/60 hover:text-white hover:bg-white/[0.08] transition-colors"
              aria-label="菜单"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                {mobileMenuOpen ? (
                  <>
                    <line x1="3" y1="3" x2="13" y2="13" />
                    <line x1="13" y1="3" x2="3" y2="13" />
                  </>
                ) : (
                  <>
                    <line x1="2" y1="4.5" x2="14" y2="4.5" />
                    <line x1="2" y1="8" x2="14" y2="8" />
                    <line x1="2" y1="11.5" x2="14" y2="11.5" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <div className="sm:hidden bg-primary-800 border-t border-white/[0.06] animate-slide-down">
            {/* Mobile search */}
            <div className="px-4 pt-3 pb-2">
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="搜索..."
                  className="w-full h-9 pl-3.5 pr-9 text-sm rounded bg-white/[0.08] text-primary-50 placeholder-primary-50/30 border border-white/[0.06] outline-none focus:border-white/20 transition-colors"
                />
                <button
                  onClick={handleSearch}
                  className="absolute right-3 text-primary-50/40 hover:text-primary-50/70 transition-colors"
                  aria-label="搜索"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <circle cx="6.5" cy="6.5" r="4.5" />
                    <line x1="10.5" y1="10.5" x2="14" y2="14" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Mobile nav */}
            <nav className="px-2 pb-3">
              {NAV_ITEMS.map((item) => {
                const isActive = item.path === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.path);

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={handleNavClick}
                    className={clsx(
                      "flex items-center justify-between px-3 py-2.5 rounded text-sm transition-colors",
                      isActive
                        ? "text-white bg-white/[0.08]"
                        : "text-primary-50/50 hover:text-primary-50/80 hover:bg-white/[0.04]"
                    )}
                  >
                    <span className="font-medium">{item.label}</span>
                    <span className="text-2xs text-primary-50/30">{item.desc}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {/* ── Main Content ── */}
      <main className="flex-1 max-w-[1440px] mx-auto w-full px-4 sm:px-6 py-4 sm:py-5 overflow-x-hidden">
        <ErrorBoundary>
          {children}
        </ErrorBoundary>
      </main>

      {/* Upload dialog */}
      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </div>
  );
}
